import { supabaseClient } from "@/lib/database/supabase";
import parseOAuthFromRoute from "@/lib/utils/route/parseOAuthFromRoute";
import { Theme } from "@shared/lib/constants/themes";
import { PlayerWithCrewMemberships } from "@shared/lib/database/query.types";
import { Session, SignInWithOAuthCredentials, SignInWithPasswordlessCredentials, Subscription } from "@supabase/supabase-js";
import { defineStore } from "pinia";
import { computed, ref, watch } from "vue";
import { RouteLocationNormalizedGeneric } from "vue-router";
import useAnalyticsStore from "./useAnalyticsStore";

export default defineStore("AuthStore", () => {
  const {
    identify,
    reset,
  } = useAnalyticsStore();

  const isSessionLoaded = ref(false);
  const session = ref<Session | null>(null);
  const subscription = ref<Subscription>();
  const isPlayerLoaded = ref(false);
  const currentPlayer = ref<PlayerWithCrewMemberships | null>(null);

  const authHeaders = computed(() => {
    return {
      Authorization: `Bearer ${session.value?.access_token}`,
    };
  });
  const currentUser = computed(() => session.value?.user);
  const isSignedIn = computed(() => !!currentUser.value);
  const theme = computed(() => currentPlayer.value?.theme as keyof typeof Theme | null | undefined);
  const isCommissioner = computed(() => !!currentPlayer.value?.crew_memberships.some((membership) => membership.role === "commissioner"));

  watch(() => currentUser.value?.id, async (userId) => {
    if (userId) {
      const {
        data: player,
        error,
      } = await supabaseClient
        .from("players")
        .select("*, crew_memberships:player_crews(*, crew:crews!inner(*))")
        .eq("user_id", userId)
        .limit(1)
        .maybeSingle();

      if (!error) {
        currentPlayer.value = player || null;
        isPlayerLoaded.value = true;
      }
    } else {
      currentPlayer.value = null;
      isPlayerLoaded.value = false;
    }
  }, {
    immediate: true,
  });

  watch(currentPlayer, () => {
    if (currentPlayer.value && currentPlayer.value.user_id) {
      identify(currentPlayer.value.user_id, {
        name: currentPlayer.value.name,
        email: currentPlayer.value.email,
        player_id: currentPlayer.value.id,
        stripe_customer_id: currentPlayer.value.stripe_customer_id,
        supporter_type: currentPlayer.value.supporter_type,
        theme: currentPlayer.value.theme,
        position: currentPlayer.value.position,
        is_run_summary_notification_enabled: currentPlayer.value.is_run_summary_notification_enabled,
        is_updated_statuses_notification_enabled: currentPlayer.value.is_updated_statuses_notification_enabled,
      });
    }
  });

  async function waitForSession () {
    return new Promise((resolve) => {
      if (isSessionLoaded.value) {
        resolve(session.value);
        return;
      }

      const cancelWatch = watch(isSessionLoaded, () => {
        if (isSessionLoaded.value) {
          cancelWatch();
          resolve(session.value);
        }
      });
    });
  }

  async function waitForPlayer () {
    return new Promise((resolve) => {
      if (isPlayerLoaded.value) {
        resolve(currentPlayer.value);
        return;
      }

      const cancelWatch = watch(isPlayerLoaded, () => {
        if (isPlayerLoaded.value) {
          cancelWatch();
          resolve(currentPlayer.value);
        }
      });
    });
  }

  async function subscribeToAuthChanges () {
    const {
      data,
    } = await supabaseClient.auth.onAuthStateChange(async (event, _session) => {
      session.value = _session;

      if (event === "INITIAL_SESSION") {
        isSessionLoaded.value = true;
      } else if (event === "SIGNED_IN" && session.value?.user) {
        const {
          user,
        } = session.value;

        identify(user.id, {}, {
          name: user.user_metadata.name,
          email: user.email,
          created_at: user.created_at,
        });

        const newSourceUrl = user.user_metadata?.avatar_url ?? null;
        if (!newSourceUrl) return;

        waitForPlayer().then(() => {
          if (
            currentPlayer.value
            && newSourceUrl !== currentPlayer.value.avatar_source_url
          ) {
            syncAvatar(user.id, newSourceUrl);
          }
        });
      } else if (event === "SIGNED_OUT") {
        reset();
      }
    });

    subscription.value = data.subscription;
  }

  function getAvatarUrlAtSize (sourceUrl: string, size: number) {
    return sourceUrl.replace(/=s\d+-c$/, `=s${size}-c`);
  }

  async function syncAvatar (userId: string, sourceUrl: string) {
    const normalizedSourceUrl = getAvatarUrlAtSize(sourceUrl, 512);
    const response = await fetch(normalizedSourceUrl, {
      referrerPolicy: "no-referrer",
    });
    if (!response.ok) return;

    const blob = await response.blob();
    const extension = blob.type.split("/")[1] ?? "jpg";
    const newPath = `${userId}/${Date.now()}.${extension}`;

    const {
      error: uploadError,
    } = await supabaseClient.storage
      .from("avatars")
      .upload(newPath, blob, {
        cacheControl: String(365 * 24 * 60 * 60),
        upsert: false,
        contentType: blob.type,
      });

    if (uploadError) return;

    const {
      data: existingFiles,
    } = await supabaseClient.storage
      .from("avatars")
      .list(userId);

    const stalePaths = (existingFiles ?? [])
      .map((file) => `${userId}/${file.name}`)
      .filter((path) => path !== newPath);

    if (stalePaths.length > 0) {
      await supabaseClient.storage
        .from("avatars")
        .remove(stalePaths);
    }

    await supabaseClient
      .from("players")
      .update({
        avatar_storage_path: newPath,
        avatar_source_url: sourceUrl,
      })
      .eq("user_id", userId);

    if (currentPlayer.value) {
      currentPlayer.value.avatar_storage_path = newPath;
      currentPlayer.value.avatar_source_url = sourceUrl;
    }
  }

  function unsubscribeFromAuthChanges () {
    subscription.value?.unsubscribe();
  }

  async function fetchSession () {
    const {
      data:{
        session: _session,
      },
    } = await supabaseClient.auth.getSession();

    session.value = _session;

    return _session;
  }

  async function setSessionFromRoute (route: RouteLocationNormalizedGeneric) {
    const {
      access_token,
      refresh_token,
    } = parseOAuthFromRoute(route);

    if (access_token && refresh_token) {
      const {
        data:{
          session: _session,
        },
      } = await supabaseClient.auth.setSession({
        access_token,
        refresh_token,
      });

      // TODO: handle errors

      session.value = _session;
    }
  }

  async function signInWithEmail (email: string, options: SignInWithPasswordlessCredentials["options"] = {}) {
    return supabaseClient.auth.signInWithOtp({
      email,
      options,
    });
  }

  async function signInWithGoogle (options: Omit<SignInWithOAuthCredentials, "provider">["options"] = {}) {
    return supabaseClient.auth.signInWithOAuth({
      provider: "google",
      options,
    });
  }

  async function signInWithApple (options: Omit<SignInWithOAuthCredentials, "provider">["options"] = {}) {
    return supabaseClient.auth.signInWithOAuth({
      provider: "apple",
      options,
    });
  }

  async function signOut () {
    const {
      error,
    } = await supabaseClient.auth.signOut();

    if (!error) {
      session.value = null;
    }
  }

  return {
    session,
    authHeaders,
    currentUser,
    currentPlayer,
    isSignedIn,
    isPlayerLoaded,
    isCommissioner,
    theme,

    subscribeToAuthChanges,
    unsubscribeFromAuthChanges,
    fetchSession,
    setSessionFromRoute,
    signInWithEmail,
    signInWithGoogle,
    signInWithApple,
    signOut,
    waitForSession,
    waitForPlayer,
  };
});
